import { useMutation, useQuery } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { useSession } from '@/auth/index';
import { useAppConstantsContext } from '@/context/AppConstants';
import { request } from '@/utils/requestHelpers';
import { EXPORT_ENDPOINT, potentialOwnerReports } from '../constants';

type RefetchFunction = () => void;

const fetchCSVs = async (permalink: string) => {
  if (!permalink.length) return;
  const url = `${EXPORT_ENDPOINT}/${permalink}`;

  return await request(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(() => {
    throw new Error('Error fetching CSVs');
  });
};

const downloadExport = async (permalink: string, reportTypes: string[]) => {
  if (!permalink.length) return;
  let filename: string;
  const isSingleExport =
    reportTypes.length === 1 ||
    reportTypes.every((reportType) =>
      potentialOwnerReports.includes(reportType)
    );

  const exportFormat = isSingleExport ? 'csv' : 'zip';
  return await fetch(`${EXPORT_ENDPOINT}/${permalink}.${exportFormat}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `Unexpected response on export download: ${response.status} - ${response.statusText}`
        );
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      return response.blob();
    })
    .then((blob) => {
      saveAs(blob, filename);
      return blob;
    })
    .catch((error) => {
      throw new Error(
        `Error downloading CSV: ${error.status} - ${error.statusText}`
      );
    });
};

const formatListName = (list: string) => {
  return list.trim().replace(/\W+/g, '_').toLowerCase();
};

const useExportMutation = (permalinks: string[], lists: string[]) => {
  const exportReports = async () => {
    const reponse = await fetch(EXPORT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        report_export: {
          report_permalinks: permalinks,
          format: 'csv',
          user_code: userCode,
          brand_name: brandName,
          export_name:
            lists?.length === 1 ? formatListName(lists.shift() || '') : null,
        },
      }),
    }).catch(() => {
      throw new Error('Error exporting reports');
    });

    const data = await reponse.json();
    return data;
  };

  const {
    session: { account },
  } = useSession();
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const userCode = account?.account.user_info?.user_code;
  const {
    data,
    isError: isErrorMutation,
    isSuccess: isSuccessMutation,
    mutate,
  } = useMutation({
    mutationFn: exportReports,
  });

  return { data, isSuccessMutation, isErrorMutation, mutate };
};

export const useFetchCSV = (
  permalink: string,
  refetchDownload: RefetchFunction,
  setIsLoading: (isLoading: boolean) => void
) => {
  const {
    refetch: refetchCSVData,
    isFetching: isFetchingFetchCSV,
    isError: isErrorFetchCSV,
  } = useQuery({
    queryKey: ['csvs'],
    queryFn: () => fetchCSVs(permalink),
    enabled: false,
    onSuccess: (data) => {
      if (data?.meta?.status === 202) {
        setTimeout(() => {
          refetchCSVData();
        }, data.polling.rate * 1000);
      } else if (data.meta?.status === 200) {
        refetchDownload();
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  return { refetchCSVData, isErrorFetchCSV, isFetchingFetchCSV };
};

export const useDownloadCSV = (
  permalink: string,
  reportTypes: string[],
  setIsLoading: (isLoading: boolean) => void
) => {
  const {
    refetch: refetchDownload,
    isSuccess: isSuccessDownloadCSV,
    isError: isErrorDownloadCSV,
    isFetching: isFetchingDownloadCSV,
  } = useQuery({
    queryKey: ['blobs'],
    queryFn: () => downloadExport(permalink, reportTypes),
    enabled: false,
    onSettled: () => {
      setIsLoading(false);
    },
  });

  return {
    refetchDownload,
    isFetchingDownloadCSV,
    isErrorDownloadCSV,
    isSuccessDownloadCSV,
  };
};

export const useExport = (
  permalinks: string[],
  lists: string[],
  reportTypes: string[]
) => {
  const uniquePermalinks = Array.from(new Set(permalinks));
  const uniqueReportTypes = Array.from(new Set(reportTypes));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    data: exportReportData,
    isErrorMutation,
    isSuccessMutation,
    mutate,
  } = useExportMutation(uniquePermalinks, lists);
  const permalink = exportReportData?.report?.permalink;
  const { refetchDownload, isSuccessDownloadCSV, isErrorDownloadCSV } =
    useDownloadCSV(permalink, uniqueReportTypes, setIsLoading);
  const { refetchCSVData, isErrorFetchCSV } = useFetchCSV(
    permalink,
    refetchDownload,
    setIsLoading
  );

  useEffect(() => {
    if (exportReportData?.report?.permalink) {
      setIsLoading(true);
      refetchCSVData();
    }
  }, [exportReportData]);

  return {
    mutateCSVs: mutate,
    isErrorMutation,
    isErrorFetchCSV,
    isErrorDownloadCSV,
    isSuccessDownloadCSV,
    isSuccessMutation,
    isLoading,
  };
};
